import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemErro } from '../utils';
import FormRecebedor, { type DadosRecebedor } from './FormRecebedor';

interface Recebedor {
  id: number;
  nome: string;
  tipo_chave: string;
  chave_mascarada: string;
  banco: string | null;
  documento_mascarado: string | null;
  usado_em: number;
}

const ROTULO_TIPO_CHAVE: Record<string, string> = {
  cpf: 'CPF',
  cnpj: 'CNPJ',
  email: 'E-mail',
  telefone: 'Telefone',
  aleatoria: 'Aleatória',
};

const AVISO_TROCA =
  'Você trocou quem recebe depois que já havia pagamento aprovado. Os pagamentos antigos continuam com quem recebia antes; os novos vão para este recebedor.';

interface BlocoRecebedorProps {
  eventoId: number | null;
  aoMudarTem?: (tem: boolean) => void;
}

export default function BlocoRecebedor({ eventoId, aoMudarTem }: BlocoRecebedorProps) {
  const [recebedores, setRecebedores] = useState<Recebedor[]>([]);
  const [recebedorSelecionado, setRecebedorSelecionado] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroCarregar, setErroCarregar] = useState<string | null>(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [usando, setUsando] = useState(false);

  useEffect(() => {
    if (!eventoId) {
      setCarregando(false);
      return;
    }
    let ativo = true;

    async function carregar() {
      setCarregando(true);
      setErroCarregar(null);
      try {
        const [resList, resDoEvento, resEvento] = await Promise.all([
          supabase.rpc('meus_recebedores'),
          supabase.rpc('recebedor_do_evento', { p_evento: eventoId }),
          supabase.from('eventos').select('recebedor_id').eq('id', eventoId).maybeSingle(),
        ]);
        if (!ativo) return;
        if (resList.error) throw resList.error;
        if (resDoEvento.error) throw resDoEvento.error;
        if (resEvento.error) throw resEvento.error;

        const lista = (resList.data as Recebedor[]) || [];
        setRecebedores(lista);
        setMostrarForm(lista.length === 0);

        const tem = (resDoEvento.data as { tem?: boolean } | null)?.tem === true;
        aoMudarTem?.(tem);

        const idAtual =
          (resEvento.data as { recebedor_id?: number | null } | null)?.recebedor_id ?? null;
        if (idAtual !== null) setRecebedorSelecionado(idAtual);
        else setRecebedorSelecionado(lista.length > 0 ? lista[0].id : null);
      } catch (e) {
        if (ativo) {
          setErroCarregar(
            mensagemErro(e, 'Não foi possível carregar quem recebe.'),
          );
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  async function escolherRecebedor(id: number) {
    if (!eventoId) return;
    setUsando(true);
    setAviso(null);
    setErroSalvar(null);
    try {
      const { data, error } = await supabase.rpc('usar_recebedor', {
        p_evento: eventoId,
        p_recebedor: id,
      });
      if (error) throw error;
      setRecebedorSelecionado(id);
      aoMudarTem?.(true);
      if (
        data === 'aviso' ||
        (data && typeof data === 'object' && (data as Record<string, unknown>).aviso)
      ) {
        setAviso(AVISO_TROCA);
      }
    } catch (e) {
      setErroSalvar(mensagemErro(e, 'Não foi possível escolher quem recebe.'));
    } finally {
      setUsando(false);
    }
  }

  async function salvarRecebedor(dados: DadosRecebedor) {
    if (!eventoId) return;
    setSalvando(true);
    setErroSalvar(null);
    try {
      const { data, error } = await supabase.rpc('salvar_recebedor', { p: dados });
      if (error) throw error;
      const resultado = data as { ok?: boolean; erro?: string; id?: number } | null;
      if (!resultado || resultado.ok === false) {
        setErroSalvar(mensagemErro(resultado?.erro, 'Não foi possível salvar quem recebe.'));
        return;
      }

      const { data: listaData, error: errLista } = await supabase.rpc('meus_recebedores');
      if (errLista) throw errLista;
      setRecebedores((listaData as Recebedor[]) || []);
      setMostrarForm(false);
      setErroSalvar(null);

      if (resultado.id) {
        await escolherRecebedor(resultado.id);
      }
    } catch (e) {
      setErroSalvar(mensagemErro(e, 'Não foi possível salvar quem recebe.'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="rounded-lg border border-secondary-200 bg-white p-5">
      <h4 className="text-[15px] font-medium text-foreground-950">Para onde vai o dinheiro</h4>
      <p className="mt-1 text-sm text-secondary-500">
        Escolha a chave Pix que vai receber os repasses da plataforma.
      </p>

      {carregando ? (
        <div className="mt-3 space-y-2">
          <div className="h-14 w-full animate-pulse rounded-lg bg-secondary-100" />
          <div className="h-14 w-full animate-pulse rounded-lg bg-secondary-100" />
        </div>
      ) : erroCarregar ? (
        <p className="mt-3 rounded-md bg-danger-50 px-3 py-2.5 text-sm text-danger-700">
          {erroCarregar}
        </p>
      ) : mostrarForm || recebedores.length === 0 ? (
        <div className="mt-3">
          <FormRecebedor
            erro={erroSalvar}
            salvando={salvando}
            aoSalvar={salvarRecebedor}
            aoCancelar={recebedores.length > 0 ? () => setMostrarForm(false) : undefined}
          />
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {recebedores.map((r) => {
            const selecionado = recebedorSelecionado === r.id;
            const partes = [ROTULO_TIPO_CHAVE[r.tipo_chave] || r.tipo_chave, r.chave_mascarada];
            if (r.documento_mascarado && r.documento_mascarado !== r.chave_mascarada) {
              partes.push(r.documento_mascarado);
            }
            if (r.banco) partes.push(r.banco);
            if (r.usado_em > 0) {
              partes.push(`usado em ${r.usado_em} ${r.usado_em === 1 ? 'evento' : 'eventos'}`);
            }
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => escolherRecebedor(r.id)}
                disabled={usando}
                className={`w-full cursor-pointer rounded-lg border p-3.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  selecionado
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-secondary-200 bg-white hover:border-primary-300'
                }`}
              >
                <span className="flex items-start gap-2.5">
                  <span
                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                      selecionado ? 'border-primary-500' : 'border-secondary-300'
                    }`}
                  >
                    {selecionado && <span className="h-2 w-2 rounded-full bg-primary-500" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-medium text-foreground-950">
                      {r.nome}
                    </span>
                    <span className="mt-0.5 block text-sm text-secondary-500">
                      {partes.join(' · ')}
                    </span>
                  </span>
                </span>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => {
              setAviso(null);
              setErroSalvar(null);
              setMostrarForm(true);
            }}
            className="mt-1 flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-add-line" />
            </span>
            Cadastrar outra
          </button>
        </div>
      )}

      {aviso && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-amber-700">
            <i className="ri-information-line text-lg" />
          </span>
          <p className="text-sm text-amber-800">{aviso}</p>
        </div>
      )}

      <p className="mt-4 flex items-start gap-2 text-sm text-secondary-500">
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-400">
          <i className="ri-information-line" />
        </span>
        Pedimos a chave Pix do Mercado Pago porque é para ela que o repasse é feito depois do
        evento.
      </p>
    </div>
  );
}